import type { DestroyOptions } from 'pixi.js';
import { Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import { applyLayoutState } from 'libs/layouts/populateLayouts/applyLayoutState';
import { LoadingSpinnerView } from '../../views/LoadingSpinnerView';
import type { AppStateController } from '../../app/state/AppStateController';
import { type IAPProductId, type IAPProduct } from '../../app/iap/types';
import { ShopOfferView } from './ShopOfferView';
import ScrollBehaviour, { Direction } from '../../utils/ScrollBehaviour';
import { ShopInventoryLayout } from '../../../layouts/mainMenu/shop/ShopInventoryLayout';
import { ShopNavigationLayout } from '../../../layouts/mainMenu/shop/ShopNavigationLayout';
import { ShopCoinOfferView } from './ShopCoinOfferView';
import { ShopFreeGiftOfferView } from './ShopFreeGiftOfferView';
import { ScrollTapPassthrough } from '../../views/ScrollInputPassthrough';
import { MS_IN_DAY } from '../../utils/time-utils';
import type { PurchasesList } from '../../libs/offerSystem/OfferSystemHistory';
import { OfferTag } from '../../app/offers/offersTypes';
import { InventoryItem } from '../../app/resources/types';
import { GAME_ECONOMY_SETTINGS } from '../../game/GameConstants';
import { findPreferredOffer, getMostExpensivePurchaseCost } from '../../app/offers/offersUtils';

export interface ShopInventoryViewConfig {
  getShopCatalog(): Promise<IAPProduct[] | null>;
  state: AppStateController;
  onProductPurchased?: Signal<(productId: IAPProductId) => void>;
  purchaseHistory: Promise<PurchasesList>;
}

enum ShopNavigationState {
  MoreOffers = 'more_offers',
  BackToTop = 'back_to_top',
}

export class ShopInventoryView extends Container {
  public readonly onOffersVisible = new Signal<(products: IAPProduct[]) => void>();

  public readonly onItemClicked = new Signal<(product: IAPProduct) => void>();

  public readonly onFreeGiftClaimed = new Signal();

  public readonly onFreeGiftRefreshed = new Signal();

  public readonly onFreeGiftShown = new Signal();

  public readonly onExpanded = new Signal();

  private layout: ShopInventoryLayout | null = null;

  private scrollBehaviour: ScrollBehaviour | null = null;

  private cumulativeHeight = 0;

  private isCuratedOffers = true;

  private purchaseHistory: PurchasesList | null = null;

  private readonly shownProducts: string[] = [];

  constructor(private readonly config: ShopInventoryViewConfig) {
    super();

    this.initializeLayout();
    this.initializeScrollBehaviour();

    const spinner = new LoadingSpinnerView().props({
      parent: this.layout?.spinnerSlot,
      y: 0,
    });

    void this.config.getShopCatalog().then(async (products) => {
      this.purchaseHistory = await this.config.purchaseHistory;

      if (this.destroyed) return;
      if (!products) return;

      this.setupOffers(this.getCuratedShopOffers(products));
      this.setupNavigationLayout(ShopNavigationState.MoreOffers);

      this.updateScroll();
      this.scrollBehaviour?.setActive(true);

      spinner.destroy();
    });
  }

  private initializeLayout(): void {
    this.layout = new ShopInventoryLayout();
    const { root, scrollInput } = this.layout;
    this.addChild(root);
    scrollInput.cursor = 'default';
  }

  private initializeScrollBehaviour(): void {
    if (!this.layout) return;

    this.scrollBehaviour = new ScrollBehaviour({
      inputLayer: this.layout.scrollInput,
      renderer: this.pixi.renderer,
      target: this.layout.offerContainer,
      verticalScroll: { min: 0, max: 0 },
      rubberBanding: true,
      wheelDirection: Direction.Vertical,
    });

    void new ScrollTapPassthrough(this.layout.scrollInput, this.layout.offerContainer);
  }

  private setupOffers(products: IAPProduct[]): void {
    if (this.destroyed || !this.layout) return;

    this.onOffersVisible.emit(products.filter((product) => !this.shownProducts.includes(product.id)));
    this.shownProducts.push(...products.map((product) => product.id));

    products.forEach((product) => {
      if (this.destroyed || !this.layout) return;

      if (product) {
        let offerView: ShopOfferView | ShopCoinOfferView | null = null;
        if (product.data.offerSimpleView) {
          offerView = new ShopCoinOfferView(product);
        } else {
          offerView = new ShopOfferView(product);
          offerView.onOfferExpired.connect(async () => this.refreshOffers());
        }

        offerView.props({
          parent: this.layout.offerContainer,
          y: this.cumulativeHeight + offerView.height * 0.5,
        });

        offerView.onOfferClicked.connect((offer) => {
          this.onItemClicked.emit(offer);
        });

        this.cumulativeHeight += offerView.height;
      }
    });

    this.setupFreeGiftOffer();
  }

  private setupFreeGiftOffer(): void {
    const {
      config: { state },
    } = this;

    if (!this.layout) return;

    const freeGiftView = new ShopFreeGiftOfferView(
      state,
      InventoryItem.Coin,
      GAME_ECONOMY_SETTINGS.freeShopGiftCoinReward,
    );
    freeGiftView.props({
      parent: this.layout.offerContainer,
      y: this.cumulativeHeight + freeGiftView.height * 0.5,
    });

    freeGiftView.onFreeGiftClaimed.connect(() => {
      this.onFreeGiftClaimed.emit();
    });

    freeGiftView.onFreeGiftAnimationFinished.connect(() => {
      void this.refreshOffers();
    });

    freeGiftView.onFreeGiftRefreshed.connect(() => {
      this.onFreeGiftRefreshed.emit();
      void this.refreshOffers();
    });

    this.cumulativeHeight += freeGiftView.height;

    this.onFreeGiftShown.emit();
  }

  private clearOffers(): void {
    if (!this.layout) return;
    this.layout.offerContainer.removeChildren();
    this.cumulativeHeight = 0;
  }

  private async refreshOffers(): Promise<void> {
    this.clearOffers();

    const products = await this.config.getShopCatalog();
    if (!products) return;

    if (this.isCuratedOffers) {
      this.setupOffers(this.getCuratedShopOffers(products));
      this.setupNavigationLayout(ShopNavigationState.MoreOffers);
    } else {
      this.setupOffers(products);
      this.setupNavigationLayout(ShopNavigationState.BackToTop);
    }
  }

  private setupNavigationLayout(navigationState: ShopNavigationState): void {
    const navigationLayout = new ShopNavigationLayout();
    applyLayoutState(navigationLayout, navigationState);
    navigationLayout.root.props({
      parent: this.layout?.offerContainer,
      y: this.cumulativeHeight + navigationLayout.root.height * 0.5,
    });

    switch (navigationState) {
      case ShopNavigationState.MoreOffers:
        navigationLayout.root.on('pointertap', async (): Promise<void> => {
          navigationLayout.root.off('pointertap');
          this.scrollBehaviour?.setActive(false);
          this.clearOffers();
          const products = await this.config.getShopCatalog();
          if (!products) return;

          this.setupOffers(products);
          this.onExpanded.emit();
          this.setupNavigationLayout(ShopNavigationState.BackToTop);
          this.updateScroll();
          this.scrollBehaviour?.setActive(true);
          this.isCuratedOffers = false;
        });
        break;
      case ShopNavigationState.BackToTop:
        navigationLayout.root.on('pointertap', async (): Promise<void> => {
          await this.scrollBehaviour?.scrollToTop();
        });
        break;
      default:
        break;
    }
  }

  public resize(_width: number, _height: number): void {
    if (!this.layout) return;

    this.updateScroll();
  }

  private updateScroll(): void {
    if (!this.layout) return;

    const offersContainer = this.layout.offerContainer;
    const minY = Math.min(0, -(offersContainer.height - this.pixi.renderer.height * 0.5));

    this.scrollBehaviour?.updateConfig({
      verticalScroll: { max: 0, min: minY },
      wheelDirection: Direction.Vertical,
    });
  }

  public getCuratedShopOffers(products: IAPProduct[]): IAPProduct[] {
    if (!this.purchaseHistory) return [];

    const mostExpensivePurchase = getMostExpensivePurchaseCost(this.purchaseHistory, 10 * MS_IN_DAY);

    const curatedOffers: IAPProduct[] = [];

    const activeStarterOffer = products.find((product) => product.tags.includes(OfferTag.StarterBundle));

    const safetyNet = products.find((product) => product.tags.includes(OfferTag.SafetyNet));

    // safety net on top
    if (safetyNet) {
      curatedOffers.push(safetyNet);
    }

    // 1. add starter bundle if exists
    if (activeStarterOffer) {
      curatedOffers.push(activeStarterOffer);
    }

    // 2. add time-limited offer closest but more expensive than highest recent purchase
    const timeLimitedOffers = products.filter((product) => product.tags.includes(OfferTag.UpsellBundle));
    let preferredOffer = findPreferredOffer(timeLimitedOffers, mostExpensivePurchase);

    if (preferredOffer) {
      curatedOffers.push(preferredOffer);
    }

    // fill curatedOffers with 2-3 offers
    if (curatedOffers.length < 2) {
      // 3. add basic bundle closest but more expensive than highest recent purchase
      const basicBundleOffers = products.filter((product) => product.tags.includes(OfferTag.BasicBundle));
      preferredOffer = findPreferredOffer(basicBundleOffers, mostExpensivePurchase);

      if (preferredOffer) {
        curatedOffers.push(preferredOffer);
      }
    }

    // 4. Add coin package closest but more expensive than highest recent purchase
    const coinOffers = products.filter((product) => product.tags.includes(OfferTag.BasicGold));
    preferredOffer = findPreferredOffer(coinOffers, mostExpensivePurchase);

    if (preferredOffer) {
      curatedOffers.push(preferredOffer);
    }

    return curatedOffers;
  }

  public destroy(options?: DestroyOptions): void {
    this.onItemClicked.disconnectAll();
    this.scrollBehaviour?.setActive(false);
    super.destroy(options);
  }
}
